// features/tutorials/components/StepNavigator.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Circle, Play, Lock } from 'lucide-react';
import type { LessonStep } from '../types';

interface StepNavigatorProps {
    steps: LessonStep[];
    currentStepIndex: number;
    completedSteps: number[];
    onStepClick: (index: number) => void;
}

export const StepNavigator: React.FC<StepNavigatorProps> = ({
    steps,
    currentStepIndex,
    completedSteps,
    onStepClick,
}) => {
    return (
        <div className="p-4">
            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4 px-3">
                Lesson Steps
            </h3>
            <nav className="space-y-1">
                {steps.map((step: LessonStep, index: number) => {
                    const isCompleted = completedSteps.includes(index);
                    const isCurrent = currentStepIndex === index;
                    const isAccessible = isCompleted || index <= Math.max(...completedSteps, 0) + 1;

                    return (
                        <motion.button
                            key={index}
                            onClick={() => isAccessible && onStepClick(index)}
                            whileHover={isAccessible ? { x: 3 } : {}}
                            className={`w-full flex items-start space-x-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${!isAccessible
                                ? 'opacity-50 cursor-not-allowed'
                                : isCurrent
                                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 shadow-sm'
                                    : isCompleted
                                        ? 'text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/10'
                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                }`}
                            disabled={!isAccessible}
                        >
                            <div className="flex-shrink-0 mt-0.5">
                                {isCompleted ? (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ type: 'spring', stiffness: 300 }}
                                    >
                                        <CheckCircle className="w-5 h-5 text-green-500" />
                                    </motion.div>
                                ) : isCurrent ? (
                                    <motion.div
                                        animate={{ scale: [1, 1.1, 1] }}
                                        transition={{ duration: 1.5, repeat: Infinity }}
                                        className="relative"
                                    >
                                        <div className="absolute inset-0 bg-blue-400 rounded-full blur-sm opacity-50" />
                                        <Play className="w-5 h-5 text-blue-500 relative" />
                                    </motion.div>
                                ) : isAccessible ? (
                                    <Circle className="w-5 h-5 text-slate-300 dark:text-slate-600" />
                                ) : (
                                    <Lock className="w-5 h-5 text-slate-300 dark:text-slate-600" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">
                                    {step.title}
                                </div>
                                <div className="text-xs opacity-75 mt-0.5">
                                    {step.type === 'video' ? '📹 Video' :
                                        step.type === 'interactive' ? '🎮 Interactive' :
                                            step.type === 'image' ? '🖼️ Visual' : '📖 Reading'}
                                </div>
                            </div>
                        </motion.button>
                    );
                })}
            </nav>

            {/* Progress Summary */}
            <div className="mt-6 px-3">
                <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                    Progress
                </div>
                <div className="w-full h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${(completedSteps.length / steps.length) * 100}%` }}
                        transition={{ duration: 0.5 }}
                    />
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    {completedSteps.length} of {steps.length} steps completed
                </div>
            </div>
        </div>
    );
};